// FUNCTIONALITY-115B-R5 — Availability request V2 in-app bottom sheet.
import { useEffect, useMemo, useState } from "react";
import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";
import { ProductThumb } from "./components.js";
import { resolveCustomerVisiblePrice } from "./resolve-customer-visible-price-v2.js";
import { resolveProductAvailability } from "./product-availability-v2.js";
import { formatUah } from "./money-v2.js";
import {
  validateAvailabilityAttachment,
  type AvailabilityAttachment,
} from "./availability-attachment-v2.js";
import { customerCodeLabel, resolveCustomerFacingCode } from "./product-identity-v2.js";

const PHONE_RE = /^\+?[0-9()\-\s]{10,18}$/;

export interface AvailabilityRequestSheetProps {
  lang: StoreV2Lang;
  open: boolean;
  product: CatalogV2Product | null;
  displayTitle: string;
  prefPhone: string | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: {
    phone: string;
    comment: string;
    attachment: AvailabilityAttachment | null;
    honeypot: string;
    interactionMs: number;
    idempotencyKey: string;
  }) => void;
}

export function AvailabilityRequestSheet({
  lang,
  open,
  product,
  displayTitle,
  prefPhone,
  busy,
  onClose,
  onSubmit,
}: AvailabilityRequestSheetProps) {
  const [phone, setPhone] = useState(prefPhone ?? "");
  const [comment, setComment] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [openedAt, setOpenedAt] = useState(() => Date.now());
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [attachment, setAttachment] = useState<AvailabilityAttachment | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPhone(prefPhone ?? "");
    setComment("");
    setHoneypot("");
    setOpenedAt(Date.now());
    setIdempotencyKey(
      `av-${product?.id ?? "x"}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
    );
    setAttachment(null);
    setAttachError(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [open, prefPhone, product?.id]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const phoneEditable = useMemo(() => {
    const trimmed = (prefPhone ?? "").trim();
    return !trimmed || !PHONE_RE.test(trimmed);
  }, [prefPhone]);

  if (!open || !product) return null;

  const availability = resolveProductAvailability(product);
  const price = resolveCustomerVisiblePrice(product);
  const title = lang === "en" ? "Ask availability" : "Запитати наявність";
  const explain =
    lang === "en"
      ? "We will confirm availability, the current price, and a possible supply timeline."
      : "Ми уточнимо наявність, актуальну ціну та можливий строк постачання.";
  const attachHint =
    lang === "en"
      ? "Optionally add a photo or screenshot of the product you are looking for."
      : "За потреби додайте фото або скрин товару, який ви шукаєте.";

  return (
    <div className="sv2-sheet-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" className="sv2-sheet-backdrop" aria-label={t(lang, "close")} onClick={onClose} />
      <div className="sv2-sheet-panel">
        <div className="sv2-sheet-handle" aria-hidden="true" />
        <div className="sv2-section-title">{title}</div>
        <p className="sv2-muted" style={{ marginBottom: 10 }}>
          {explain}
        </p>

        <div className="sv2-avail-sheet-product">
          <ProductThumb product={product} className="sv2-avail-sheet-img" alt={displayTitle} />
          <div>
            <div className="sv2-card-title" style={{ textAlign: "left" }}>
              {displayTitle}
            </div>
            {product.brand ? (
              <div className="sv2-muted">
                {t(lang, "brand")}: {product.brand}
              </div>
            ) : null}
            <div className="sv2-muted">
              {(() => {
                const code = resolveCustomerFacingCode({
                  merchantSku: product.merchantSku,
                  merchantArticle: product.merchantArticle,
                  barcodeGtin: product.barcodeGtin,
                  barcodeVerification: product.barcodeVerification,
                });
                return code ? `${customerCodeLabel(lang, code.kind)}: ${code.value}` : null;
              })()}
            </div>
            {product.weight ? (
              <div className="sv2-muted">
                {t(lang, "weight")}: {product.weight}
              </div>
            ) : null}
            <div className="sv2-card-price" style={{ marginTop: 4 }}>
              {product.isPriced ? formatUah(price.primary) : t(lang, "price_tbd")}
            </div>
            <div className={`sv2-availability sv2-availability-${availability.state}`}>
              {lang === "en"
                ? availability.state === "available"
                  ? t(lang, "available")
                  : availability.state === "unavailable"
                    ? t(lang, "not_available")
                    : t(lang, "availability_unknown")
                : availability.labelUa}
            </div>
          </div>
        </div>

        <label className="sv2-field">
          <span>{t(lang, "phone")}</span>
          <input
            type="tel"
            inputMode="tel"
            value={phone}
            disabled={!phoneEditable || busy}
            onChange={(e) => setPhone(e.target.value.slice(0, 32))}
            placeholder="+380XXXXXXXXX"
            autoComplete="tel"
          />
        </label>

        {/* Honeypot — hidden from humans; bots fill it. */}
        <label className="sv2-hp" aria-hidden="true">
          <span>Company</span>
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>

        <label className="sv2-field">
          <span>
            {t(lang, "note")} ({t(lang, "optional")})
          </span>
          <textarea
            value={comment}
            maxLength={500}
            rows={3}
            disabled={busy}
            onChange={(e) => setComment(e.target.value)}
            placeholder={lang === "en" ? "Optional comment" : "Необовʼязковий коментар"}
          />
        </label>

        <label className="sv2-about-link sv2-receipt-picker">
          📎 {attachHint}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            disabled={busy}
            onChange={async (event) => {
              const file = event.target.files?.[0] ?? null;
              setAttachError(null);
              setPreviewUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
              });
              setAttachment(null);
              if (!file) return;
              const validated = await validateAvailabilityAttachment(file, lang);
              if (!validated.ok) {
                setAttachError(validated.error);
                return;
              }
              setAttachment(validated.attachment);
              setPreviewUrl(URL.createObjectURL(file));
            }}
          />
        </label>
        {previewUrl ? (
          <div className="sv2-receipt-preview">
            <img src={previewUrl} alt="" className="sv2-receipt-thumb" />
            {attachment ? <div className="sv2-muted">{attachment.fileName}</div> : null}
          </div>
        ) : null}
        {attachError ? <div className="sv2-alert sv2-alert-err">{attachError}</div> : null}

        <button
          type="button"
          className="sv2-cta"
          disabled={busy || (phoneEditable && !PHONE_RE.test(phone.trim()))}
          onClick={() =>
            onSubmit({
              phone: phone.trim(),
              comment: comment.trim(),
              attachment,
              honeypot,
              interactionMs: Math.max(0, Date.now() - openedAt),
              idempotencyKey,
            })
          }
        >
          {busy
            ? lang === "en"
              ? "Sending…"
              : "Надсилаємо…"
            : lang === "en"
              ? "Send request to manager"
              : "Надіслати запит менеджеру"}
        </button>
        <button type="button" className="sv2-cta sv2-secondary" disabled={busy} onClick={onClose}>
          {lang === "en" ? "Cancel" : "Скасувати"}
        </button>
      </div>
    </div>
  );
}
